<template>
  <b-container fluid>
    <b-row
      v-if="!hideSearch"
      align-h="end"
    >
      <b-col
        cols="12"
        sm="12"
        md="8"
        lg="6"
        xl="4"
      >
        <s-search-input
          class="mb-3"
          v-model="tempSearch"
          v-s-trigger="searchChange"
          :placeholder="$t('reports.oscillography.view-report.search')"
        />
      </b-col>
    </b-row>

    <b-row
      v-for="(maneuver, index) in filteredManeuvers"
      :key="getManeuverKey(maneuver, index)"
    >
      <b-col cols="12">
        <info-report-group
          :maneuver="maneuver"
          :maneuver-key="getManeuverKey(maneuver, index)"
          :fields="getDiagnosticsFields(maneuver)"
          :isDefaultChart="isDefaultChart"
          :isDiagnostics="true"
          :apply-diagnostic-limits="applyDiagnosticLimits"
          class="mb-3"
        />
      </b-col>
    </b-row>
    <b-row
      v-if="!filteredManeuvers.length"
    >
      <b-col cols="12">
        <s-empty-list :loading="loading" />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { cleanIncludes, clearDiacritics } from '@/services/stringHelper';
import InfoReportGroup from './InfoReportGroup';

export default {
  components: {
    InfoReportGroup,
  },
  props: {
    reportData: {
      type: Object,
      required: true,
    },
    isDefaultChart: {
      type: Boolean,
      default: false,
    },
    hideSearch: {
      type: Boolean,
      default: false,
    },
    applyDiagnosticLimits: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tempSearch: null,
    search: null,
  }),
  methods: {
    compareManeuverDates(first, second) {
      const firstTimestamp = first?.dateTimeOscillography
        ? new Date(first.dateTimeOscillography).getTime()
        : NaN;
      const secondTimestamp = second?.dateTimeOscillography
        ? new Date(second.dateTimeOscillography).getTime()
        : NaN;

      if (!Number.isFinite(firstTimestamp)) {
        return Number.isFinite(secondTimestamp) ? 1 : 0;
      }

      if (!Number.isFinite(secondTimestamp)) {
        return -1;
      }

      return secondTimestamp - firstTimestamp;
    },
    getManeuverKey(maneuver, index) {
      if (maneuver.maneuverKey) {
        return maneuver.maneuverKey;
      }

      const maneuverIdentifier = maneuver.maneuverUUID
        || maneuver.maneuverId
        || maneuver.name;
      const moduleIdentifier = maneuver.assetModuleId
        || maneuver.moduleName;

      return moduleIdentifier
        ? `${maneuverIdentifier}-${moduleIdentifier}`
        : maneuverIdentifier || index;
    },
    searchChange(search) {
      this.search = search;
    },
    filterManeuver({ name }) {
      return cleanIncludes(name, this.effectiveQuery, {
        clearValueToFind: false,
      });
    },
    filterManeuverFields(maneuverFields) {
      return maneuverFields.filter(({ name, value }) => cleanIncludes(value, this.effectiveQuery, {
        clearValueToFind: false,
      }) || cleanIncludes(name, this.effectiveQuery, {
        clearValueToFind: false,
      }));
    },
    getDiagnosticsFields(maneuver) {
      if (maneuver.infoData && maneuver.infoData.diagnostics) {
        return maneuver.infoData.diagnostics;
      }

      return [];
    },
  },
  computed: {
    maneuvers() {
      return this.reportData.maneuvers || [];
    },
    filteredManeuvers() {
      const maneuversWithDiagnostics = this.maneuvers.filter(maneuver => {
        const diagnosticsFields = this.getDiagnosticsFields(maneuver);
        return diagnosticsFields.some(field => field.hasValue);
      }).sort((first, second) => this.compareManeuverDates(first, second));

      if (!this.effectiveQuery) {
        return maneuversWithDiagnostics;
      }

      return maneuversWithDiagnostics.reduce((localFilteredManeuvers, maneuver) => {
        if (this.filterManeuver(maneuver)) {
          localFilteredManeuvers.push(maneuver);
          return localFilteredManeuvers;
        }

        const diagnosticsFields = this.getDiagnosticsFields(maneuver);
        const filteredManeuverFields = this.filterManeuverFields(diagnosticsFields);
        if (filteredManeuverFields.length) {
          localFilteredManeuvers.push({
            ...maneuver,
            infoData: {
              ...maneuver.infoData,
              diagnostics: filteredManeuverFields,
            },
          });
        }

        return localFilteredManeuvers;
      }, []);
    },
    effectiveQuery() {
      return this.search && this.search.length >= 2
        ? clearDiacritics(this.search).toLowerCase()
        : null;
    },
  },
};
</script>
